import * as React from 'react';
import styled from 'styled-components';
import { IoMdClose } from 'react-icons/io';
import { useEffect, useState } from 'react';
import { StaticImage } from 'gatsby-plugin-image';
import { StrapiTextual } from '@/features/strapi/components/textual';
import { TalkToExpertContent } from '@/features/new-landing-page/pages/talk-to-expert-content';
import { VisuallyLogo } from '@/features/strapi/components/logo';
import {
  HubspotField,
  HubspotForm,
} from '@/features/new-landing-page/components/hubspot-form';
import { useDetachedState } from '@/components/hooks/use-detached-state';
import { AllLogos } from '@/features/new-landing-page/components/customers-scroller';
import { breakpoints } from '@/components/responsive';
import { VSpace } from '@/components/spacing';
import { MainBannerContent } from '@/features/new-landing-page/pages/homepage-content';
import { StrapiImageTextual } from '@/features/strapi/components/image-textual';
import { HorizontalLayout } from '@/features/new-landing-page/components/section';
import { HubspotCalendar } from '@/features/new-landing-page/components/hubspot-calendar';
import { changeUrlNoHistory } from '@/utils/browser';

export interface BookDemoOverlayProps {
  email?: string;
  onClose?: () => void;
}

export function BookDemoOverlay({ email, onClose }: BookDemoOverlayProps) {
  const [formRefState, setFormRef, formRef] =
    useDetachedState<HTMLFormElement>(null);
  const [showCalendar, setShowCalendar] = useState(false);

  useEffect(() => {
    if (formRef?.current && email) {
      const emailInput = formRef.current.querySelector(
        `input[name="email"]`,
      ) as HTMLInputElement;
      if (emailInput) {
        emailInput.value = email;
        emailInput.dispatchEvent(new Event(`change`, { bubbles: true }));
        emailInput.dispatchEvent(new Event(`input`, { bubbles: true }));
      }
    }
  }, [email, formRefState]);

  const handleSubmit = (form: HTMLFormElement, fields: HubspotField[]) => {
    const monthlyOrders = fields.find((f) => f.name === `avg__monthly_orders`);

    if (monthlyOrders && monthlyOrders.value) {
      const qualifyingOptions = [
        `3,000-5,000 orders`,
        `5,000-7,500 orders`,
        `7,500-15,000 orders`,
        `15,000-30,000 orders`,
        `30,000+ orders`,
        `We are an agency`,
      ];
      if (qualifyingOptions.includes(monthlyOrders.value)) {
        try {
          if (monthlyOrders.value?.toLowerCase()?.includes(`agency`)) {
            changeUrlNoHistory(
              `https://www.visually.io/public/meeting-form-submit-agency`,
            );
          } else {
            changeUrlNoHistory(
              `https://www.visually.io/public/meeting-form-submit-icp`,
            );
          }
        } finally {
          setShowCalendar(true);
        }
        return;
      }
    }

    changeUrlNoHistory(`https://www.visually.io/public/meeting-form-submit-ss`);
  };

  useEffect(() => {
    const handler = (ev) => {
      if (
        ev?.data?.type === `hs-meeting-booked` ||
        ev?.data?.meetingBookSucceeded === true
      ) {
        changeUrlNoHistory(`/public/demo-scheduled`);
      }
    };
    window.addEventListener(`message`, handler);

    return () => {
      window.removeEventListener(`message`, handler);
    };
  }, []);

  return (
    <Wrapper>
      <IoMdClose className="close-btn" size={35} onClick={onClose} />
      <Left>
        <ContentWrapper>
          <VisuallyLogo wide />
          <StrapiTextual {...TalkToExpertContent.form.title} />
          <StrapiTextual {...TalkToExpertContent.form.subTitle} />
          <FormWrapper>
            {!showCalendar ? (
              <HubspotForm
                formId="12741ed6-6c57-481f-8c87-e36253d85734"
                portalId="143410811"
                submitText="Schedule a call"
                onFormReady={(form) => {
                  setFormRef(form);
                }}
                onFormSubmit={handleSubmit}
                additionalCss={FORM_CSS}
              />
            ) : (
              <CalendarOverlay>
                <IoMdClose className="close-btn" size={35} onClick={onClose} />
                <HubspotCalendar url="https://meetings-eu1.hubspot.com/marc-adelman" />
              </CalendarOverlay>
            )}
          </FormWrapper>
          <HorizontalLayout gap="2rem" className="benefits">
            {MainBannerContent.benefitUsps.map((usp) => (
              <StrapiImageTextual
                key={usp.textual.text}
                {...usp}
                gap="0.3rem"
              />
            ))}
          </HorizontalLayout>
        </ContentWrapper>
      </Left>
      <Right>
        <RightContentWrapper>
          <VSpace value={1.5} />
          <StrapiTextual {...TalkToExpertContent.form.logosTitle} />
          <LogosWrapper className="only-desktop desktop-logos">
            <StaticImage
              src="../assets/all_logos.png"
              alt="Visually Customers"
              loading="eager"
              placeholder="blurred"
              width={1000}
            />
          </LogosWrapper>
          <LogosWrapper className="only-mobile">
            <AllLogos />
          </LogosWrapper>
          <VSpace value={2} className="only-mobile" />
        </RightContentWrapper>
      </Right>
    </Wrapper>
  );
}

export const FORM_CSS = `
  input[type="text"], input[type="email"], textarea {
    border: 1px solid rgb(193,193,204)!important;
    border-radius: 100px!important;
    background: white!important;
    padding: 22px 20px!important;
    font-size: 14px!important;
    color: black;
  }

  input[type="text"]::placeholder, input[type="email"]::placeholder, textarea::placeholder, select::placeholder {
    color: rgb(49,53,57)!important;
  }

  select {
    background: white!important;
    border-radius: 100px!important;
    width: 100%!important;
    height: 46px!important;
    border: 1px solid rgb(193,193,204)!important;
    color: black!important;
    appearance: none!important;
    font-size: 14px!important;
    background: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="rgb(49,53,57)" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M6 9l6 6 6-6"/></svg>') no-repeat right 18px center!important;
    background-size: 20px!important;
    padding-left: 20px!important;
  }

  select.invalid {
    color: rgb(49,53,57)!important;
  }

  input.hs-button[type="submit"] {
    background: black!important;
    border-radius: 100px !important;
    color: white !important;
    border-color: black!important;
    font-family: "Helvetica Neue",Helvetica,Arial,sans-serif;
  }

  .submitted-message {
    color: black!important;
    text-align: center!important;
  }
`;

const Wrapper = styled.div`
  position: fixed;
  inset: 0;
  height: 100vh;
  width: 100vw;
  z-index: 99999999;

  display: grid;
  grid-template-columns: 1fr 1fr;

  .close-btn {
    position: absolute;
    top: 4rem;
    right: 4rem;
    cursor: pointer;
    transition: transform 0.2s;
  }

  .close-btn:hover {
    transform: scale(1.1);
  }

  ${breakpoints.down(`md`)} {
    display: flex;
    flex-direction: column;

    .close-btn {
      top: 2rem;
      right: 2rem;
    }

    overflow-y: scroll;
    max-height: 100vh;
  }
`;

const Left = styled.div`
  background: white;

  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  width: 100%;

  ${breakpoints.down(`md`)} {
    padding: 8vh 0;
  }
`;

const Right = styled.div`
  background: #f6f5f4;
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  width: 100%;

  ${breakpoints.down(`md`)} {
    padding: 6vh 0;
  }
`;

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;

  min-width: 55%;
  max-width: 55%;

  gap: 2rem;

  && * {
    user-select: none;
  }

  .strapi-textual {
    text-align: start;
  }

  .benefits {
    margin-top: -1rem;
  }

  ${breakpoints.down(`md`)} {
    min-width: 100%;
    max-width: 100%;

    padding: 0 10vw;

    .benefits {
      margin-left: -1rem;
    }
  }
`;

const RightContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;

  min-width: 70%;
  max-width: 70%;

  gap: 2rem;

  && * {
    user-select: none;
  }

  .desktop-logos img {
    height: 100%;
    width: 100%;
    object-fit: contain;
  }

  ${breakpoints.down(`md`)} {
    min-width: 80%;
    max-width: 80%;
    gap: 4rem;
  }
`;

const LogosWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;

  margin-top: -2rem;
  .gatsby-image-wrapper {
    width: 90%;
  }

  ${breakpoints.down(`md`)} {
    margin-top: 0rem;
    row-gap: 3rem;
    column-gap: 3rem;

    display: grid;
    grid-template-columns: repeat(2, 1fr);
    grid-auto-rows: 6rem;

    .gatsby-image-wrapper {
      width: 100%;
    }
  }
`;

const CalendarOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: white;
  z-index: 1000000;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const FormWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 100%;
  margin-top: 1rem;
  min-height: 40rem;

  .hubspot-form {
    width: 100%;
  }
`;
